/**
 * Tráfego real no Painel (fase 2, modo ao vivo): a tabela de campanhas, conjuntos e anúncios vem da
 * Meta (gasto, impressões, cliques, visitas, situação, orçamento e miniatura) cruzada com as vendas
 * reais da Kiwify pela UTM (`GET /api/painel/insights`). Funil próprio (quiz, VSL, checkout) fica
 * zerado até o rastreador e o player entrarem.
 */
import type {
  ChannelReport,
  ChannelRow,
  Filters,
  Freshness,
  InsightLevel,
  InsightQuery,
  InsightRow,
  InsightTable,
  MetaEffectiveStatus,
  SalesChannel,
  SuiteFreshness,
} from '@rota/contracts';

import { getWithRetry } from './http';

const DAY = 86_400_000;

/** Linha como o servidor manda (valores em centavos de real; dólar já convertido). */
export interface RealInsightRow {
  level: 'campaign' | 'adset' | 'ad';
  id: string;
  name: string;
  accountId: string;
  campaignId: string | null;
  adsetId: string | null;
  status: string | null;
  /** Nomes do conjunto e da campanha (níveis abaixo da campanha); ausentes em servidor antigo. */
  adsetName?: string | null;
  campaignName?: string | null;
  /** Anúncio: 1º dia com gasto do mesmo criativo em todo o histórico; ausente em servidor antigo. */
  firstSpendDate?: string | null;
  /** Orçamento diário em centavos de real (servidor novo: a conta em dólar já vem convertida). */
  dailyBudget: number | null;
  /** Orçamento como está na Meta, em centavos da moeda da conta; ausente em servidor antigo. */
  budgetNative?: number | null;
  /**
   * Moeda dos valores da linha. Servidor novo: sempre "BRL" (tudo já em real) e a moeda da conta vai
   * em `accountCurrency`; servidor antigo: a moeda da conta.
   */
  currency?: string | null;
  /** Moeda da conta na Meta (a do `budgetNative`); ausente em servidor antigo. */
  accountCurrency?: string | null;
  /** Fuso da conta: fora de São Paulo, o dia da linha é o dia da conta (ex.: Nova York). */
  timezone?: string | null;
  thumbnail: string | null;
  createdAt: number | null;
  spendCents: number;
  /** Custo real (impostos da Meta na conta em real; câmbio do cartão + IOF na de dólar); ausente em servidor antigo. */
  costCents?: number;
  impressions: number;
  /**
   * Alcance (pessoas únicas). Não soma: o servidor manda null quando a linha junta mais de um anúncio
   * ou mais de um dia (servidor antigo mandava a soma, que deixava a frequência baixa demais).
   */
  reach: number | null;
  clicks: number;
  linkClicks: number;
  lpv: number;
  frontSales: number;
  upsellSales: number;
  grossCents: number;
  commissionCents: number;
  /** Dia a dia da linha (períodos de mais de um dia; ausente em servidor antigo). */
  daily?: RealInsightDay[];
  /** Visualizações de 3 s (Meta); ausente em servidor antigo. */
  videoViews?: number;
  /** Finalizações de compra iniciadas (Meta); ausente em servidor antigo. */
  checkouts?: number;
  /** Valor das compras pelo preço do produto; ausente em servidor antigo. */
  valueCents?: number;
  /** Compras e valor como a Meta atribui (Gerenciador de Anúncios); ausentes em servidor antigo. */
  metaPurchases?: number;
  /** Valor das compras da Meta com o preço cheio (as que vieram com a comissão, corrigidas). */
  metaValueCents?: number;
  /** Valor como a Meta registrou. */
  metaValueRawCents?: number;
  /**
   * Compras da Meta que são do 20X (o servidor separa pelo valor de cada compra, anúncio a anúncio e
   * dia a dia); ausente em servidor antigo.
   */
  metaUpsell?: number;
}

/** Um dia de uma linha (o dia da conta, como a tabela): gasto, custo, atividade e compras da Meta. */
export interface RealInsightDay {
  date: string;
  spendCents: number;
  costCents: number;
  impressions: number;
  linkClicks: number;
  lpv: number;
  metaPurchases: number;
  metaUpsell: number;
  metaValueCents: number;
}

/** Nome curto de cada conta de anúncios (como o dono chama). Conta nova: "Conta" e o fim do número. */
const ACCOUNT_NAMES: Readonly<Record<string, string>> = {
  act_451177072626810: 'CA 01',
  act_2532436727231820: 'YO 98',
};
export const accountName = (id: string): string =>
  ACCOUNT_NAMES[id] ?? `Conta ${id.replace(/^act_/, '').slice(-4)}`;

// ---------- recorte por conta, campanha, conjunto e anúncio ----------

/** Algum filtro de conta, campanha, conjunto ou anúncio (o recorte que a Meta entende). */
export const hasEntityFilter = (f: Filters): boolean =>
  !!(f.accountIds?.length || f.campaignIds?.length || f.adsetIds?.length || f.adIds?.length);

const campaignOf = (r: RealInsightRow): string | null => (r.level === 'campaign' ? r.id : r.campaignId);
const adsetOf = (r: RealInsightRow): string | null =>
  r.level === 'adset' ? r.id : r.level === 'ad' ? r.adsetId : null;

/**
 * Linhas que passam no filtro de conta, campanha, conjunto e anúncio (o id de cada nível sai de um
 * campo diferente: na linha de campanha, o próprio id é a campanha). Linha mais grossa que o filtro
 * (campanha com filtro de anúncio) não dá para recortar e passa: quem pede um nível mais grosso com
 * filtro mais fino soma as linhas finas antes (`rollUp`).
 */
export function filterRows(rows: readonly RealInsightRow[], f: Filters): RealInsightRow[] {
  const has = (ids: readonly string[] | undefined, id: string | null) => id != null && !!ids?.includes(id);
  return rows.filter(
    (r) =>
      (!f.accountIds?.length || f.accountIds.includes(r.accountId)) &&
      (!f.campaignIds?.length || has(f.campaignIds, campaignOf(r))) &&
      (!f.adsetIds?.length || r.level === 'campaign' || has(f.adsetIds, adsetOf(r))) &&
      (!f.adIds?.length || r.level !== 'ad' || f.adIds.includes(r.id)),
  );
}

/** Nível das linhas que o recorte precisa para um nível pedido (filtro de anúncio pede as linhas de anúncio). */
export function cutLevel(level: 'campaign' | 'adset' | 'ad', f: Filters): 'campaign' | 'adset' | 'ad' {
  if (f.adIds?.length && level !== 'ad') return 'ad';
  if (f.adsetIds?.length && level === 'campaign') return 'adset';
  return level;
}

/**
 * Junta linhas finas (já recortadas) no nível pedido: a campanha com só os anúncios do filtro. Nome,
 * situação, orçamento e miniatura vêm da linha do próprio nível (`own`); os números, da soma.
 */
export function rollUp(
  fine: readonly RealInsightRow[],
  level: 'campaign' | 'adset',
  own: readonly RealInsightRow[],
): RealInsightRow[] {
  const byId = new Map(own.map((r) => [r.id, r]));
  const groups = new Map<string, RealInsightRow[]>();
  for (const r of fine) {
    const id =
      (level === 'campaign' ? r.campaignId : r.adsetId) ??
      (level === 'campaign' ? 'sem_campanha' : 'sem_conjunto');
    groups.set(id, [...(groups.get(id) ?? []), r]);
  }
  return [...groups].map(([id, rs]) => {
    const m = byId.get(id);
    const first = rs[0] as RealInsightRow;
    const sum = sumRows(rs, { id, name: m?.name ?? id, accountId: first.accountId });
    return {
      ...sum,
      level,
      campaignId: level === 'campaign' ? id : first.campaignId,
      adsetId: level === 'adset' ? id : null,
      status: m?.status ?? sum.status,
      dailyBudget: m?.dailyBudget ?? null,
      ...(m?.budgetNative !== undefined ? { budgetNative: m.budgetNative } : {}),
      ...(m?.currency !== undefined ? { currency: m.currency } : {}),
      ...(m?.accountCurrency !== undefined ? { accountCurrency: m.accountCurrency } : {}),
      timezone: m?.timezone ?? sum.timezone ?? null,
      thumbnail: m?.thumbnail ?? null,
      createdAt: m?.createdAt ?? null,
    };
  });
}

/**
 * Dia a dia de um conjunto de linhas (AAAA-MM-DD → soma). Linha sem o detalhe por dia (período de um
 * dia só) conta inteira no dia `single`. Custo ausente (servidor antigo): o gasto com o imposto.
 */
export function daysOf(
  rows: readonly RealInsightRow[],
  single: string,
  metaTaxPct: number,
): Map<string, RealInsightDay> {
  const out = new Map<string, RealInsightDay>();
  const add = (d: RealInsightDay) => {
    const cur = out.get(d.date);
    if (!cur) {
      out.set(d.date, { ...d });
      return;
    }
    cur.spendCents += d.spendCents;
    cur.costCents += d.costCents;
    cur.impressions += d.impressions;
    cur.linkClicks += d.linkClicks;
    cur.lpv += d.lpv;
    cur.metaPurchases += d.metaPurchases;
    cur.metaUpsell += d.metaUpsell;
    cur.metaValueCents += d.metaValueCents;
  };
  for (const r of rows) {
    if (r.daily) {
      for (const d of r.daily) add(d);
      continue;
    }
    add({
      date: single,
      spendCents: r.spendCents,
      costCents: r.costCents ?? Math.round(r.spendCents / (1 - metaTaxPct)),
      impressions: r.impressions,
      linkClicks: r.linkClicks,
      lpv: r.lpv,
      metaPurchases: r.metaPurchases ?? 0,
      metaUpsell: r.metaUpsell ?? 0,
      metaValueCents: r.metaValueCents ?? 0,
    });
  }
  return out;
}

/**
 * Frescor da Meta como o servidor manda: `lastSyncAt` = último sucesso; `syncError` = a última tentativa
 * falhou; `todayAt` = último sucesso que trouxe hoje; `dataAsOf` = linha mais nova do período;
 * `incompleteDays` = dias fechados lidos antes de terminar. Os quatro últimos faltam em servidor antigo.
 */
export interface MetaSyncFields {
  lastSyncAt: number | null;
  syncError?: string | null;
  todayAt?: number | null;
  dataAsOf?: number | null;
  incompleteDays?: string[];
}

export interface RealInsights extends MetaSyncFields {
  generatedAt: number;
  rows: RealInsightRow[];
  /** Vendas com link de anúncio sem campanha/anúncio conhecidos (fora da tabela). */
  untrackedSales: number;
  /** As mesmas separadas em curso e 20X e o valor delas; ausentes em servidor antigo. */
  untrackedFront?: number;
  untrackedUpsell?: number;
  untrackedValueCents?: number;
  /**
   * Todas as vendas pagas com link de anúncio (com e sem campanha encontrada), pela UTM da Kiwify;
   * ausente em servidor antigo. Referência: a venda de anúncio conta pela Meta.
   */
  adChannelSales?: AdChannelSales;
  utmContentCoverage: number | null;
  /**
   * Dias do período com menos finalizações de compra que compras numa conta (evento de checkout
   * faltando na Meta desde 27/10/2026); ausente em servidor antigo.
   */
  checkoutGapDays?: string[];
}

/** Vendas pagas com link de anúncio (UTM): todas, curso, 20X e o valor dos produtos. */
export interface AdChannelSales {
  paid: number;
  front: number;
  upsell: number;
  valueCents: number;
}

export type FetchInsights = (
  from: number,
  to: number,
  level: 'campaign' | 'adset' | 'ad',
) => Promise<RealInsights | null>;

/** Erro de fonte fora do ar (`ProviderError` com `source_unavailable`): a tela mostra o erro, nunca a simulação. */
export const metaUnavailable = (message: string): Error & { code: 'source_unavailable' } =>
  Object.assign(new Error(message), { code: 'source_unavailable' as const });

export const fetchRealInsights: FetchInsights = async (from, to, level) => {
  const r = await getWithRetry(`/api/painel/insights?from=${from}&to=${to}&level=${level}`);
  if (!r.ok) throw metaUnavailable(`Meta com erro (HTTP ${r.status})`);
  return (await r.json()) as RealInsights;
};

/**
 * Campos a mais da linha ao vivo, fora do contrato: o orçamento como está na Meta (moeda da conta) e o
 * fuso da conta. A tela lê com `liveExtras(row)`.
 */
export interface LiveRowExtras {
  budgetNative?: number | null;
  budgetCurrency?: string | null;
  accountTz?: string | null;
  /**
   * Etapas do funil sem fonte real por objeto (quiz e VSL: o rastreador e a VTurb não separam por
   * anúncio). O contrato pede número; o sheet tira essas etapas em vez de mostrar 0.
   */
  funnelUnmeasured?: readonly FunnelUnmeasured[];
  /** As vendas da linha são as compras que a Meta atribui (não as da Kiwify pelo link). */
  salesBy?: 'meta';
}
export type FunnelUnmeasured = 'quizStart' | 'quizComplete' | 'vslPlay' | 'pitch';
export const liveExtras = (row: InsightRow): LiveRowExtras => row as InsightRow & LiveRowExtras;

/**
 * Campos a mais da tabela ao vivo (e do total dela): as vendas com link de anúncio sem campanha
 * encontrada (fora das linhas), separadas, e todas as vendas com link de anúncio do período.
 */
export interface LiveTableExtras {
  untrackedFront?: number;
  untrackedUpsell?: number;
  untrackedValue?: number;
  adChannel?: AdChannelSales;
}
export const liveTableExtras = (t: InsightTable | InsightRow): LiveTableExtras =>
  t as (InsightTable | InsightRow) & LiveTableExtras;

/**
 * Finalizações de compra que não fecham: abaixo das compras da Meta (toda compra passa pelo checkout).
 * Desde 27/10/2026 o evento InitiateCheckout quase não chega à Meta (5 checkouts para 58 compras):
 * aí o número é "não medido", não um funil que despenca.
 */
export const checkoutsBroken = (
  checkouts: number | undefined,
  purchases: number | null | undefined,
): boolean => (purchases ?? 0) > 0 && (checkouts ?? 0) < (purchases ?? 0);

const STATUSES = new Set<string>([
  'ACTIVE',
  'PAUSED',
  'DELETED',
  'ARCHIVED',
  'IN_PROCESS',
  'WITH_ISSUES',
  'CAMPAIGN_PAUSED',
  'ADSET_PAUSED',
  'PENDING_REVIEW',
  'DISAPPROVED',
  'PREAPPROVED',
  'PENDING_BILLING_INFO',
]);

/** Situação da Meta como o contrato conhece; desconhecida, arquivada. */
export const effectiveStatus = (s: string | null): MetaEffectiveStatus =>
  s && STATUSES.has(s) ? (s as MetaEffectiveStatus) : 'ARCHIVED';

const div = (a: number, b: number, k = 1): number | null => (b > 0 ? (a / b) * k : null);

type Sum = Omit<
  RealInsightRow,
  | 'level'
  | 'id'
  | 'name'
  | 'accountId'
  | 'campaignId'
  | 'adsetId'
  | 'status'
  | 'dailyBudget'
  | 'budgetNative'
  | 'currency'
  | 'accountCurrency'
  | 'timezone'
  | 'thumbnail'
  | 'createdAt'
  | 'adsetName'
  | 'campaignName'
  | 'firstSpendDate'
>;
const SUM_KEYS: Exclude<
  keyof Sum,
  | 'reach'
  | 'daily'
  | 'videoViews'
  | 'checkouts'
  | 'valueCents'
  | 'metaPurchases'
  | 'metaValueCents'
  | 'metaValueRawCents'
  | 'metaUpsell'
  | 'costCents'
>[] = [
  'spendCents',
  'impressions',
  'clicks',
  'linkClicks',
  'lpv',
  'frontSales',
  'upsellSales',
  'grossCents',
  'commissionCents',
];

const results = (r: RealInsightRow) => r.metaPurchases ?? r.frontSales + r.upsellSales;

/** Parte do produtor no faturamento (comissão ÷ faturamento da Kiwify em 27/10/2026), para linhas sem venda na Kiwify. */
const COMMISSION_SHARE = 0.33;

/**
 * Vendas de anúncio pelo que a Meta marcou (as compras do Gerenciador, com o preço cheio), não pelo
 * link da Kiwify: decisão do dono em 27/10/2026, porque a Meta registra vendas de anúncio que chegam
 * à Kiwify sem a UTM do anúncio. A Meta não diz o produto: as compras do 20X saem do valor (servidor,
 * `metaUpsell`), e "vendas" (e o CPA) contam só as do curso, porque o teto do CPA já soma o 20X por
 * comprador do curso. Servidor sem a separação: o 20X que a Kiwify ligou pela UTM. A comissão
 * acompanha o valor na proporção da Kiwify. Servidor antigo (sem dado da Meta): fica a Kiwify.
 */
export function withMetaSales(r: RealInsightRow): RealInsightRow {
  if (r.metaPurchases == null || r.metaValueCents == null) return r;
  const upsell = Math.min(r.metaUpsell ?? r.upsellSales, r.metaPurchases);
  const gross = r.metaValueCents;
  return {
    ...r,
    frontSales: r.metaPurchases - upsell,
    upsellSales: upsell,
    grossCents: gross,
    commissionCents:
      r.grossCents > 0
        ? Math.round((r.commissionCents * gross) / r.grossCents)
        : Math.round(gross * COMMISSION_SHARE),
  };
}

export function toInsightRow(
  raw: RealInsightRow,
  level: InsightLevel,
  o: {
    now: number;
    metaTaxPct: number;
    freshness: Freshness[];
    /** O recorte inteiro tem menos finalizações que compras: nenhuma linha tem o número medido. */
    checkoutsBroken?: boolean;
  },
): InsightRow {
  const r = withMetaSales(raw);
  const spend = r.spendCents;
  const media = r.costCents ?? Math.round(spend / (1 - o.metaTaxPct));
  // O anúncio é pago pela empresa com o faturamento inteiro, não só com a nossa comissão.
  const profit = r.grossCents - media;
  const status = effectiveStatus(r.status);
  const age = r.createdAt ? Math.floor((o.now - r.createdAt) / DAY) : null;
  // Checkout abaixo das compras não é medida: fica sem número (a tela mostra "não medido" e o funil
  // tira a etapa). O contrato pede número; o null aqui é de propósito.
  const checkouts =
    o.checkoutsBroken || checkoutsBroken(raw.checkouts, raw.metaPurchases)
      ? (null as unknown as number)
      : (r.checkouts ?? 0);
  const row: InsightRow = {
    level,
    id: r.id,
    name: r.name,
    accountId: r.accountId,
    status,
    dailyBudget: r.dailyBudget,
    budgetOwner: level === 'ad' || r.dailyBudget == null ? null : 'self',
    badges: [
      ...(status === 'DISAPPROVED' ? (['disapproved'] as const) : []),
      ...(level === 'ad' && age != null && age < 3 ? (['new'] as const) : []),
    ],
    spendAm: spend,
    mediaCost: media,
    impressions: r.impressions,
    // Alcance só de uma linha (um anúncio num dia): somado, sairia uma frequência baixa demais.
    reach: r.reach || null,
    frequency: r.reach ? div(r.impressions, r.reach) : null,
    cpm: div(spend, r.impressions, 1000),
    linkClicks: r.linkClicks,
    ctrLink: div(r.linkClicks, r.impressions),
    cpcLink: div(spend, r.linkClicks),
    lpv: r.lpv,
    connectRate: div(r.lpv, r.linkClicks),
    // Hook: visualizações de 3 s ÷ impressões (só quando o anúncio tem vídeo visto).
    hookRate: r.videoViews ? div(r.videoViews, r.impressions) : null,
    holdRate: null,
    creativeAgeDays: level === 'ad' ? age : null,
    quizStarts: 0,
    quizCompletes: 0,
    vslPlays: 0,
    pitchReached: 0,
    ctaClicks: 0,
    initiateCheckouts: checkouts,
    frontSales: r.frontSales,
    upsellSales: r.upsellSales,
    revenueGross: r.grossCents,
    revenueNet: r.commissionCents,
    // CPA = investimento ÷ compras do curso (o 20X separado pelo valor: o teto já o soma). Sem gasto no
    // período (a compra é de um clique de outro dia): "—", como no Gerenciador, e não R$ 0,00.
    cpa: spend > 0 && r.frontSales > 0 ? Math.round(spend / r.frontSales) : null,
    // ROAS = valor das compras da Meta ÷ valor gasto (sem imposto nem IOF), o do Gerenciador.
    roas: div(r.grossCents, spend),
    profit: media > 0 || r.grossCents > 0 ? profit : null,
    margin: div(profit, r.grossCents),
    // ROI (nome do dono): valor dos resultados ÷ investimento com imposto e IOF (o custo real da mídia).
    roi: div(r.grossCents, media),
    // Retorno real (o que fica na empresa ÷ custo de mídia) só existe na conta inteira: a taxa da
    // Kiwify e os reembolsos não são por campanha. Antes repetia o ROI com outro nome.
    roasReal: null,
    // ROAS do funil (o mesmo do Hoje): valor das compras da Meta ÷ valor gasto, como o "ROAS de
    // resultados de compra" do Gerenciador (sem imposto nem IOF; decisão do dono).
    roasFunnel: div(r.grossCents, spend),
    // Visão "Gerenciador": resultados e valor como a Meta atribui (os mesmos do Gerenciador de
    // Anúncios); sem esse dado (servidor antigo), as vendas da Kiwify pelo link.
    results: results(r),
    costPerResult: spend > 0 && results(r) > 0 ? Math.round(spend / results(r)) : null,
    resultValue: r.metaValueCents ?? r.valueCents ?? 0,
    roasValue: div(r.metaValueCents ?? r.valueCents ?? 0, spend),
    kiwifySales: raw.frontSales + raw.upsellSales,
    // As da Kiwify pelo link separadas (antes da troca pelas compras da Meta): referência e conciliação.
    kiwifyFront: raw.frontSales,
    kiwifyUpsell: raw.upsellSales,
    kiwifyValue: r.valueCents ?? 0,
    ...(r.metaValueRawCents != null ? { metaValueRaw: r.metaValueRawCents } : {}),
    costPerLpv: spend > 0 && r.lpv > 0 ? Math.round(spend / r.lpv) : null,
    pitchRetention: null,
    postPitchCtr: null,
    checkoutConv: null,
    upsellTakeRate: div(r.upsellSales, r.frontSales),
    refundRateD30: null,
    metaPurchases: r.metaPurchases ?? null,
    cpaSpark7d: [],
    status_: {},
    freshness: o.freshness,
  };
  if (level !== 'account' && level !== 'campaign' && r.campaignId) row.campaignId = r.campaignId;
  if (level === 'campaign') row.campaignId = r.id;
  if (level === 'ad' && r.adsetId) row.adsetId = r.adsetId;
  if (level === 'adset') row.adsetId = r.id;
  if (level === 'ad') row.adId = r.id;
  if (r.adsetName !== undefined) row.adsetName = r.adsetName;
  if (r.campaignName !== undefined) row.campaignName = r.campaignName;
  if (r.firstSpendDate !== undefined) row.firstSpendDate = r.firstSpendDate;
  if (r.thumbnail) row.thumbnailUrl = r.thumbnail;
  // Moeda do orçamento na Meta: a da conta (servidor novo manda à parte; a linha já vem em "BRL").
  const budgetCurrency = r.accountCurrency ?? r.currency;
  const extras: LiveRowExtras = {
    ...(r.budgetNative != null && budgetCurrency && budgetCurrency !== 'BRL'
      ? { budgetNative: r.budgetNative, budgetCurrency }
      : {}),
    ...(r.timezone && r.timezone !== 'America/Sao_Paulo' ? { accountTz: r.timezone } : {}),
    funnelUnmeasured: ['quizStart', 'quizComplete', 'vslPlay', 'pitch'],
    ...(raw.metaPurchases != null && raw.metaValueCents != null ? { salesBy: 'meta' as const } : {}),
  };
  return Object.assign(row, extras);
}

/**
 * Soma linhas (o total da tabela e o nível "conta"). O alcance não soma (a mesma pessoa vê vários
 * anúncios): fica o da linha quando é uma só; com mais de uma, null (frequência "—"). O dia a dia
 * soma por data.
 */
function sumRows(
  rows: RealInsightRow[],
  base: Pick<RealInsightRow, 'id' | 'name' | 'accountId'>,
): RealInsightRow {
  const out: RealInsightRow = {
    level: 'campaign',
    ...base,
    campaignId: null,
    adsetId: null,
    status: rows.some((r) => r.status === 'ACTIVE') ? 'ACTIVE' : 'PAUSED',
    dailyBudget: null,
    thumbnail: null,
    createdAt: null,
    // Uma conta só (nível "conta"): leva o fuso dela para o aviso do dia.
    timezone: new Set(rows.map((r) => r.timezone ?? null)).size === 1 ? (rows[0]?.timezone ?? null) : null,
    spendCents: 0,
    impressions: 0,
    reach: rows.length === 1 ? (rows[0]?.reach ?? null) : null,
    clicks: 0,
    linkClicks: 0,
    lpv: 0,
    frontSales: 0,
    upsellSales: 0,
    grossCents: 0,
    commissionCents: 0,
  };
  if (rows.some((r) => r.daily)) {
    const days = new Map<string, RealInsightDay>();
    for (const r of rows)
      for (const d of r.daily ?? []) {
        const cur = days.get(d.date);
        days.set(
          d.date,
          cur
            ? {
                ...cur,
                spendCents: cur.spendCents + d.spendCents,
                costCents: cur.costCents + d.costCents,
                impressions: cur.impressions + d.impressions,
                linkClicks: cur.linkClicks + d.linkClicks,
                lpv: cur.lpv + d.lpv,
                metaPurchases: cur.metaPurchases + d.metaPurchases,
                metaUpsell: cur.metaUpsell + d.metaUpsell,
                metaValueCents: cur.metaValueCents + d.metaValueCents,
              }
            : { ...d },
        );
      }
    out.daily = [...days.values()].sort((a, b) => a.date.localeCompare(b.date));
  }
  for (const r of rows) {
    for (const k of SUM_KEYS) out[k] += r[k];
    out.videoViews = (out.videoViews ?? 0) + (r.videoViews ?? 0);
    out.checkouts = (out.checkouts ?? 0) + (r.checkouts ?? 0);
    if (r.costCents != null) out.costCents = (out.costCents ?? 0) + r.costCents;
    out.valueCents = (out.valueCents ?? 0) + (r.valueCents ?? 0);
    if (r.metaPurchases != null) out.metaPurchases = (out.metaPurchases ?? 0) + r.metaPurchases;
    if (r.metaValueCents != null) out.metaValueCents = (out.metaValueCents ?? 0) + r.metaValueCents;
    if (r.metaValueRawCents != null)
      out.metaValueRawCents = (out.metaValueRawCents ?? 0) + r.metaValueRawCents;
    if (r.metaUpsell != null) out.metaUpsell = (out.metaUpsell ?? 0) + r.metaUpsell;
  }
  // Orçamento já em real (servidor novo): somar as contas em real e em dólar é seguro.
  const budgets = rows.filter((r) => r.status === 'ACTIVE' && r.dailyBudget != null);
  if (budgets.length) out.dailyBudget = budgets.reduce((s, r) => s + (r.dailyBudget ?? 0), 0);
  return out;
}

/**
 * Tabela de Tráfego com filtro, busca, ordem e página como a da simulação. O filtro de conta,
 * campanha, conjunto e anúncio recorta as linhas antes de tudo (o nível "conta" e o total somam só o
 * recorte).
 */
export function insightTable(
  real: RealInsights,
  q: InsightQuery,
  o: {
    now: number;
    metaTaxPct: number;
    freshness: Freshness[];
    /** Todas as linhas, sem o teto de 500 da página (a lista de criativos agrupa tudo). */
    all?: boolean;
  },
): InsightTable {
  // Checkout do recorte inteiro abaixo das compras, ou algum dia do período em que o evento não chegou
  // à Meta: nenhuma linha tem o número medido (senão um anúncio com 1 checkout parece medido ao lado
  // de 57 compras sem, e o 7D soma dias medidos com dias sem o evento: 411 "checkouts" para 199 compras).
  let src = filterRows(real.rows, q.filters ?? {});
  const all = src.reduce((s, r) => ({ c: s.c + (r.checkouts ?? 0), p: s.p + (r.metaPurchases ?? 0) }), {
    c: 0,
    p: 0,
  });
  const opts = {
    now: o.now,
    metaTaxPct: o.metaTaxPct,
    freshness: o.freshness,
    checkoutsBroken: checkoutsBroken(all.c, all.p) || (real.checkoutGapDays?.length ?? 0) > 0,
  };
  if (q.level === 'account') {
    const byAcc = new Map<string, RealInsightRow[]>();
    for (const r of src) byAcc.set(r.accountId, [...(byAcc.get(r.accountId) ?? []), r]);
    src = [...byAcc].map(([acc, rs]) => sumRows(rs, { id: acc, name: accountName(acc), accountId: acc }));
  }
  if (q.parentId) {
    const p = q.parentId;
    src = src.filter((r) => r.accountId === p || r.campaignId === p || r.adsetId === p);
  }
  if (q.search) {
    const s = q.search.toLocaleLowerCase('pt-BR');
    src = src.filter((r) => r.name.toLocaleLowerCase('pt-BR').includes(s) || r.id.includes(s));
  }
  if (q.onlyActive) src = src.filter((r) => r.status === 'ACTIVE');
  let rows = src.map((r) => toInsightRow(r, q.level, opts));
  const key = q.sort.key;
  const sign = q.sort.dir === 'asc' ? 1 : -1;
  rows = rows.sort((a, b) => {
    const va = a[key];
    const vb = b[key];
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    if (va == null && vb != null) return 1;
    if (vb == null && va != null) return -1;
    return String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR') * sign;
  });
  const offset = Math.max(0, Math.floor(q.offset));
  const limit = o.all ? rows.length : Math.max(0, Math.min(500, Math.floor(q.limit)));
  const total = toInsightRow(sumRows(src, { id: 'total', name: 'Total', accountId: '' }), q.level, opts);
  total.status = 'ACTIVE';
  total.badges = [];
  total.dailyBudget = null;
  total.budgetOwner = null;
  delete total.thumbnailUrl;
  // Vendas com link de anúncio fora das linhas (sem campanha encontrada) e todas as do período: na
  // tabela e no total (o Resumo e a Conciliação leem do total). Com filtro de objeto não se aplicam.
  const extras: LiveTableExtras = hasEntityFilter(q.filters ?? {})
    ? {}
    : {
        ...(real.untrackedFront != null ? { untrackedFront: real.untrackedFront } : {}),
        ...(real.untrackedUpsell != null ? { untrackedUpsell: real.untrackedUpsell } : {}),
        ...(real.untrackedValueCents != null ? { untrackedValue: real.untrackedValueCents } : {}),
        ...(real.adChannelSales ? { adChannel: { ...real.adChannelSales } } : {}),
      };
  Object.assign(total, extras);
  return Object.assign(
    {
      rows: rows.slice(offset, offset + limit),
      total,
      count: rows.length,
      untrackedSales: real.untrackedSales,
    },
    extras,
  );
}

/** Pedido como o canal de venda da V2 vê. */
export interface ChannelOrder {
  channel: SalesChannel;
  kind: 'front' | 'upsell' | 'premium' | 'outro';
  commissionCents: number | null;
}

/** Vendas por canal (FUN-07 / Tráfego): Kiwify real; gasto só no canal de anúncios. */
export function channelReport(
  paid: ChannelOrder[],
  spendCents: number,
  now: number,
  dataAsOf: number,
): ChannelReport {
  const order: SalesChannel[] = ['meta_ads', 'ig_bio', 'ig_dm', 'ig_story', 'ig_outro', 'sem_origem'];
  const allFront = paid.filter((o) => o.kind === 'front').length;
  const rows: ChannelRow[] = order.map((ch) => {
    const mine = paid.filter((o) => o.channel === ch);
    const front = mine.filter((o) => o.kind === 'front').length;
    const net = mine.reduce((s, o) => s + (o.commissionCents ?? 0), 0);
    return {
      channel: ch,
      clicks: null,
      quizCompletes: null,
      vslPlays: 0,
      frontSales: front,
      upsellSales: mine.filter((o) => o.kind === 'upsell').length,
      backendClosed: 0,
      revenueNet: net,
      backendGross: 0,
      spend: ch === 'meta_ads' ? spendCents : 0,
      ltvPerBuyer: front > 0 ? Math.round(net / front) : null,
      share: allFront > 0 ? front / allFront : 0,
    };
  });
  const none = rows.find((r) => r.channel === 'sem_origem')?.frontSales ?? 0;
  return {
    rows,
    posts: [],
    coverage: allFront > 0 ? 1 - none / allFront : 1,
    freshness: [
      { source: 'kiwify', latency: 'realtime', fetchedAt: now, dataAsOf, stale: false } as SuiteFreshness,
    ],
  };
}
